import { env } from "@/env";
import { logger } from "@utilities/logger";
import { getErrorMessageFromUnknownError } from "@/app/_utilities/errors";

import type { ContactSalesSubmissionPayload } from "../_components/ContactSalesV2/shared/types";

export enum ContactSalesErrorType {
  VALIDATION_ERROR = "validation_error",
  CAPTCHA_ERROR = "captcha_error",
  RATE_LIMIT_ERROR = "rate_limit_error",
  SERVER_ERROR = "server_error",
  UNKNOWN_ERROR = "unknown_error",
}

export class ContactSalesError extends Error {
  constructor(
    public type: ContactSalesErrorType,
    public statusCode?: number,
    public detail?: string,
  ) {
    super(detail || type);
    this.name = "ContactSalesError";
  }
}

export const submitContactSales = async (
  payload: ContactSalesSubmissionPayload,
  hcaptchaToken: string,
): Promise<{ success: boolean }> => {
  const url = `${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/marketing/contact-sales`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        form_data: payload,
        hcaptcha_token: hcaptchaToken,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      logger.error(null, "Backend error response", errorData);

      const detail =
        typeof errorData.detail === "string"
          ? errorData.detail
          : typeof errorData.message === "string"
            ? errorData.message
            : errorData.detail
              ? JSON.stringify(errorData.detail)
              : `Failed to submit contact sales form: ${response.status} ${response.statusText}`;

      // Categorize errors based on status code
      let errorType: ContactSalesErrorType;

      if (response.status === 400 || response.status === 422) {
        errorType = ContactSalesErrorType.VALIDATION_ERROR;
      } else if (response.status === 401 || response.status === 403) {
        errorType = ContactSalesErrorType.CAPTCHA_ERROR;
      } else if (response.status === 429) {
        errorType = ContactSalesErrorType.RATE_LIMIT_ERROR;
      } else if (response.status >= 500) {
        errorType = ContactSalesErrorType.SERVER_ERROR;
      } else {
        errorType = ContactSalesErrorType.VALIDATION_ERROR;
      }

      throw new ContactSalesError(errorType, response.status, detail);
    }

    await response.json();
    return { success: true };
  } catch (error) {
    logger.error(error instanceof Error ? error : null, "Error submitting contact sales form");

    if (error instanceof ContactSalesError) {
      throw error;
    }

    throw new ContactSalesError(ContactSalesErrorType.UNKNOWN_ERROR, undefined, getErrorMessageFromUnknownError(error));
  }
};
